import React, { Fragment, useState, useEffect, useRef, useMemo } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import classNames from 'classnames';
import { functionNoop } from 'src/utils/function/noop';
import { animated, useSpring, useSpringRef } from '@react-spring/web';
import Draggable from 'react-draggable';
import { CloseRegularIcon, CloseSmallIcon, CloseStrongIcon, DragHandleSmallIcon } from 'src/monet/icons';

import { setLocalKey, getLocalKey } from 'src/common/helpers/browser-cache';

import { FLOAT_ANIMATION_CONFIG } from 'src/document-editor/neue-bubble-menu-fixed';
import { useUpdateEffect } from 'react-use';
import { TalkToJourneyMessages } from './messages';
import { Journey } from 'src/common/interfaces/journey.interface';
import { LayoutMode } from '../types';
import { TalkToJourneyMessage } from './types';
import { TalkToJourneySettingsPanelContent } from './settings-panel-content';
import { EnhancedTalkToJourneySettingsPanelContent } from './enhanced-settings-panel-content';
import { useTalkToJourneyStore } from './store';
import { useEnhancedChatStore } from './enhanced-store';
import { ControlPanelShell } from '../components/control-panel/shell';
import { useRefCallback } from 'src/utils/react/ref-callback.hook';
import { EnhancedChatMessages } from './enhanced-messages';

type Props = {
  journeyUUID: Journey['uuid'];
  layoutMode: LayoutMode;
  openRef: React.MutableRefObject<(() => void) | undefined>;
  closeRef?: React.MutableRefObject<(() => void) | undefined>;
  children?: React.ReactNode;
  onOpen?: () => void;
  onClose?: () => void;
  onSourceClick?: (message: TalkToJourneyMessage) => void;
  findBlockByContentUuid: (uuid: string) => any;
  findSectionById: (id: string) => any;
  initialFocus?: React.RefObject<HTMLElement>;
  inEditor?: boolean;
  isReadOnly?: boolean;
  readOnlyMessages?: TalkToJourneyMessage[];
  useEnhancedChat?: boolean; // New prop to enable enhanced chat
  visitorEmail?: string; // For enhanced chat
  userContext?: Record<string, any>; // For enhanced chat
};

export const TalkToJourneyPanel = ({
  journeyUUID,
  layoutMode,
  openRef,
  closeRef,
  initialFocus,
  onSourceClick,
  findBlockByContentUuid,
  findSectionById,
  inEditor = false,
  isReadOnly = false,
  readOnlyMessages = [],
  useEnhancedChat = true,
  visitorEmail = '',
  userContext = {},
  onOpen = functionNoop,
  onClose = functionNoop,
}: Props) => {
  const internalCloseRef = useRef<() => void>();
  const activeCloseRef = closeRef || internalCloseRef;
  const [openedByDefaultAlready, setOpenedByDefaultAlready] = useState(false);

  const { name, personality, shouldGreetVisitors, messages, chatOpen, setChatOpen, variablesFromJourneyInitialized } =
    useTalkToJourneyStore((state) => ({
      name: state.name,
      personality: state.personality,
      shouldGreetVisitors: state.shouldGreetVisitors,
      messages: state.messages,
      chatOpen: state.chatOpen,
      setChatOpen: state.setChatOpen,
      variablesFromJourneyInitialized: state.variablesFromJourneyInitialized,
    }));

  const { 
    assistantName: enhancedAssistantName, 
    isOpen: enhancedChatOpen, 
    setIsOpen: setEnhancedChatOpen,
    initializeFromJourney: initEnhancedFromJourney,
  } = useEnhancedChatStore((state) => ({
    assistantName: state.assistantName,
    isOpen: state.isOpen,
    setIsOpen: state.setIsOpen,
    initializeFromJourney: state.initializeFromJourney,
  }));

  const markChatOpened = () => {
    setLocalKey(`chatbot-opened`, 'true');
  };

  const shouldOpenByDefault = useMemo(() => {
    return false;
  }, [inEditor, isReadOnly, shouldGreetVisitors, variablesFromJourneyInitialized]);

  const onClickSource = useRefCallback(
    (message: TalkToJourneyMessage) => {
      if (layoutMode !== 'web' && !inEditor) {
        // this means the user clicked on the source in the player on mobile.
        // we should close the panel.
        onDialogClose();
      }

      onSourceClick?.(message);
    },
    [layoutMode, inEditor, onSourceClick]
  );

  useEffect(() => {
    openRef.current = () => {
      markChatOpened();
      if (useEnhancedChat) {
        setEnhancedChatOpen(true);
      } else {
        setChatOpen(true);
      }
      onOpen();
    };
    activeCloseRef.current = () => {
      if (useEnhancedChat) {
        setEnhancedChatOpen(false);
      } else {
        setChatOpen(false);
      }
      onClose && onClose();
    };
  }, [openRef, onOpen, useEnhancedChat, setEnhancedChatOpen, setChatOpen]);

  useEffect(() => {
    if (shouldOpenByDefault && !openedByDefaultAlready) {
      setTimeout(() => {
        openRef.current?.();
        setOpenedByDefaultAlready(true);
      }, 2000);
    }
  }, [shouldOpenByDefault, openedByDefaultAlready]);

  const onDialogClose = () => {
    activeCloseRef.current?.();
  };

  // const _onSourceClick = (message: TalkToJourneyMessage) => {
  //   onSourceClick?.(message);
  //   setTimeout(() => {
  //     if (layoutMode !== 'web' && !inEditor) {
  //       // this means the user clicked on the source in the player.
  //       // we should close the panel.
  //       onDialogClose();
  //     }
  //   }, 200);
  // }


  const calculateHeight = () => {
    const currentMessages = useEnhancedChat ? [] : messages; // Enhanced chat handles its own sizing
    if (shouldOpenByDefault && currentMessages.length <= 1) {
      return '360px';
    } else {
      return 'calc(100vh - 96px)';
    }
  };

  const isOpen = useEnhancedChat ? enhancedChatOpen : chatOpen;
  const assistantDisplayName = useEnhancedChat ? enhancedAssistantName : name;

  return (
    <>
      <div
        className={classNames('fixed z-neue-talk-to-journey-panel flex top-[72px] right-[24px]', {
          'pointer-events-auto': isOpen,
          'pointer-events-none': !isOpen,
          'journey-theme-dark': isReadOnly,
        })}
      >
        <div
          className={classNames(
            'w-[500px] flex flex-1 flex-col bg-neue-journey-bg text-neue-journey-fg rounded-lg transition-opacity duration-400',
            {
              'opacity-0': !isOpen,
              'opacity-100': isOpen,
            }
          )}
        >
          <div
            className={classNames('transition-height duration-400', {
              'max-h-[600px] p-4': !useEnhancedChat,
              'h-[calc(100vh-96px)] max-h-[600px]': useEnhancedChat,
            })}
            style={{
              height: useEnhancedChat ? 'calc(100vh - 96px)' : calculateHeight(),
            }}
          >
            {useEnhancedChat ? (
              <EnhancedChatMessages
                opened={enhancedChatOpen}
                journeyUUID={journeyUUID}
                assistantName={enhancedAssistantName}
                personality={personality}
                onSourceClick={(message) => {
                  // Convert enhanced message to legacy format for compatibility
                  const legacyMessage: TalkToJourneyMessage = {
                    role: message.role as 'user' | 'assistant',
                    text: message.content,
                    source_content_uuid: message.metadata?.source_content_uuid,
                    source_node_uuid: message.metadata?.source_node_uuid,
                  };
                  onClickSource(legacyMessage);
                }}
                onDialogClose={onDialogClose}
                findBlockByContentUuid={findBlockByContentUuid}
                findSectionById={findSectionById}
                layoutMode={layoutMode}
                isReadOnly={isReadOnly}
                visitorEmail={visitorEmail}
                userContext={userContext}
                inEditor={inEditor}
              />
            ) : (
              <TalkToJourneyMessages
                opened={chatOpen}
                journeyUUID={journeyUUID}
                name={name}
                personality={personality}
                onSourceClick={onClickSource}
                onDialogClose={onDialogClose}
                findBlockByContentUuid={findBlockByContentUuid}
                findSectionById={findSectionById}
                layoutMode={layoutMode}
                isReadOnly={isReadOnly}
                readOnlyMessages={readOnlyMessages}
              />
            )}
          </div>
        </div>
      </div>

      {/* Settings panel appears alongside enhanced chat in editor mode */}
      {inEditor && layoutMode === 'web' && useEnhancedChat && (
        <div
          onMouseDown={(e) => e.stopPropagation()}
          className={classNames(
            'fixed z-neue-talk-to-journey-panel top-[72px] right-[532px] mr-2 transition-opacity duration-400 pointer-events-auto',
            {
              'opacity-0': !isOpen,
              'opacity-100': isOpen,
            }
          )}
        >
          <ControlPanelShell
            active={isOpen}
            title='Edit chatbot'
            subtitle='Allow recipients to talk to a bot about your Journey and its content'
          >
            <EnhancedTalkToJourneySettingsPanelContent />
          </ControlPanelShell>
        </div>
      )}
    </>
  );
};
